// frontend\src\components\analytics\AnalyticsOverstock.tsx
import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import LoadingSpinner from '../LoadingSpinner';
import { useTableSort } from './useTableSort';

interface OverstockRow {
  product_id: number;
  variation_id: number;
  name: string;
  sku: string;
  total_stock: number;
  avg_sales_per_month: number;
  months_of_stock: number | null;
  value_usd: number;
  last_sale_date: string | null;
  is_dead: boolean;
}

interface OverstockResponse {
  rows: OverstockRow[];
  monthsThreshold: number;
  totalOverstockValue: number;
  count: number;
  returned: number;
}

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);

const formatNumber = (value: number) => new Intl.NumberFormat('en-US').format(value);

const formatDate = (dateStr: string) => {
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const AnalyticsOverstock: React.FC = () => {
  const [data, setData] = useState<OverstockResponse | null>(null);
  const [monthsThreshold, setMonthsThreshold] = useState(6);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  type SortKey = 'name' | 'total_stock' | 'avg_sales_per_month' | 'months_of_stock' | 'value_usd' | 'last_sale_date';
  const { sortedRows, handleSort, indicator } = useTableSort<OverstockRow, SortKey>(
    data?.rows || [],
    (row, key) => {
      if (key === 'months_of_stock') return row.months_of_stock; // null (dead) sorts last
      return row[key];
    }
  );

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/overstock', { params: { months_threshold: monthsThreshold } });
      setData(response.data);
    } catch (err: any) {
      console.error('Overstock fetch error:', err);
      setError(err.response?.data?.error || 'Failed to fetch overstock data');
    } finally {
      setLoading(false);
    }
  }, [monthsThreshold]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <div className="analytics-card">
      <div className="analytics-card-header">
        <h3>Overstock &amp; Slow-Moving Stock</h3>
        <label className="analytics-toggle-label">
          Overstock after
          <select
            value={monthsThreshold}
            onChange={(e) => setMonthsThreshold(Number(e.target.value))}
            className="date-input"
            style={{ marginLeft: '8px' }}
          >
            <option value={3}>3 months</option>
            <option value={6}>6 months</option>
            <option value={12}>12 months</option>
          </select>
        </label>
      </div>

      {data && !loading && !error && (
        <div className="analytics-total-value" style={{ marginBottom: '12px' }}>
          <div className="analytics-total-value-row">{formatCurrency(data.totalOverstockValue)}</div>
          <div className="analytics-total-value-comparison">
            capital tied up across {formatNumber(data.count)} overstocked item{data.count === 1 ? '' : 's'}
            {data.returned < data.count ? ` (showing top ${data.returned})` : ''}
          </div>
        </div>
      )}

      {loading && <LoadingSpinner />}
      {error && <div className="analytics-error-text">Error: {error}</div>}
      {!loading && !error && data && (
        <div style={{ overflowY: 'auto', maxHeight: '480px' }}>
          <table className="analytics-table overstock-table sticky-header">
            <thead>
              <tr>
                <th onClick={() => handleSort('name')}>Product Name (SKU){indicator('name')}</th>
                <th style={{ textAlign: 'center' }} onClick={() => handleSort('total_stock')}>Stock on Hand{indicator('total_stock')}</th>
                <th style={{ textAlign: 'center' }} onClick={() => handleSort('avg_sales_per_month')}>Avg Sales / mo{indicator('avg_sales_per_month')}</th>
                <th style={{ textAlign: 'center' }} onClick={() => handleSort('months_of_stock')}>Months of Stock{indicator('months_of_stock')}</th>
                <th style={{ textAlign: 'right' }} onClick={() => handleSort('value_usd')}>Capital (USD){indicator('value_usd')}</th>
                <th onClick={() => handleSort('last_sale_date')}>Last Sale{indicator('last_sale_date')}</th>
              </tr>
            </thead>
            <tbody>
              {sortedRows.map((item) => (
                <tr key={`${item.product_id}-${item.variation_id}`}>
                  <td>
                    {item.name} ({item.sku})
                  </td>
                  <td style={{ textAlign: 'center' }}>{formatNumber(item.total_stock)}</td>
                  <td style={{ textAlign: 'center' }}>{item.avg_sales_per_month.toFixed(1)}</td>
                  <td style={{ textAlign: 'center' }}>
                    {item.is_dead || item.months_of_stock === null ? (
                      <span className="overstock-dead-tag">No recent sales</span>
                    ) : (
                      `${item.months_of_stock.toFixed(1)} mo`
                    )}
                  </td>
                  <td style={{ textAlign: 'right' }}>{formatCurrency(item.value_usd)}</td>
                  <td>{item.last_sale_date ? formatDate(item.last_sale_date) : '—'}</td>
                </tr>
              ))}
              {data.rows.length === 0 && (
                <tr>
                  <td colSpan={6} className="analytics-empty-cell">
                    No overstocked items at this threshold. Nicely balanced inventory!
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AnalyticsOverstock;
